import { useEffect, useState } from "react";
import { X } from "lucide-react";
import Coin from "./Coin";
import { ZEN_REWARD } from "../data/playtime";
import sfx from "../utils/sound";

// 5 breath cycles of 4s inhale / 4s exhale = 40 seconds total.
const CYCLES = 5;
const IN = 4;
const OUT = 4;

const BreathBreak = ({ open, onComplete, onSkip }) => {
  const [cycle, setCycle] = useState(0);
  const [phase, setPhase] = useState("in"); // in | out
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!open) {
      setCycle(0);
      setPhase("in");
      setTick(0);
      return;
    }
    sfx.pop();
    const start = performance.now();
    let raf;
    const total = CYCLES * (IN + OUT);
    const loop = () => {
      const elapsed = (performance.now() - start) / 1000;
      if (elapsed >= total) {
        cancelAnimationFrame(raf);
        sfx.fanfare();
        onComplete();
        return;
      }
      const inCycle = elapsed % (IN + OUT);
      setPhase(inCycle < IN ? "in" : "out");
      setCycle(Math.min(CYCLES, Math.floor(elapsed / (IN + OUT)) + 1));
      setTick(elapsed);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  // Scale for the breath circle
  const inCycle = tick % (IN + OUT);
  const p = phase === "in" ? inCycle / IN : 1 - (inCycle - IN) / OUT;
  const scale = 0.6 + p * 0.5; // 0.6 - 1.1
  const totalSecondsLeft = Math.max(0, Math.ceil(CYCLES * (IN + OUT) - tick));

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "linear-gradient(135deg,#0e1233 0%,#293b83 100%)" }}
      data-testid="zen-break"
    >
      <button
        onClick={onSkip}
        className="absolute top-4 right-4 rounded-full flex items-center justify-center"
        style={{
          width: 44, height: 44,
          background: "#fff", border: "3px solid #2b2b2b",
          boxShadow: "0 3px 0 #2b2b2b",
        }}
        aria-label="Close"
        data-testid="zen-skip-btn"
      >
        <X size={20} strokeWidth={3} />
      </button>

      <div className="text-center px-6">
        <div
          className="mx-auto rounded-full flex items-center justify-center"
          style={{
            width: 220,
            height: 220,
            background: "radial-gradient(circle at 30% 30%, #B8E1FF, #54A0FF)",
            border: "5px solid #fff",
            boxShadow: "0 0 60px #54A0FF88",
            transform: `scale(${scale})`,
            transition: "transform 0.9s ease-in-out",
          }}
          aria-hidden="true"
          data-testid="zen-breath-circle"
        >
          <span className="text-6xl">🧘</span>
        </div>
        <div
          className="font-display text-3xl mt-6 text-white"
          data-testid="zen-instruction"
        >
          {phase === "in" ? "Breathe in…" : "Breathe out…"}
        </div>
        <p className="mt-2 text-white/80 text-base">
          Cycle {cycle} of {CYCLES} · {totalSecondsLeft}s left
        </p>
        <div className="inline-flex items-center gap-2 mt-4 rounded-full px-4 py-1 font-display text-sm"
          style={{ background: "#54D62C", color: "#2b2b2b", border: "3px solid #2b2b2b" }}
        >
          <Coin size={16} /> +{ZEN_REWARD} for finishing
        </div>
      </div>
    </div>
  );
};

export default BreathBreak;
